import React from 'react';
import { formatCurrency, formatPercentWithSign, getValueColorClass } from '../utils/contractCalculations';
import 'react-tooltip/dist/react-tooltip.css';

/**
 * ScenarioCard Component - Displays one scenario (e.g., ±5%) with negative on left, headers in middle, positive on right
 */
function ScenarioCard({ percentage, positiveData, negativeData, isSell }) {
  // For SELL positions, the bottom row swaps from "Equity" (= close cost) to
  // "Net P&L" (= avg cost − simulated premium, in dollars). Color the value
  // by sign for sells; buys keep their current white styling because
  // simulated equity is always non-negative.
  const equityRowLabel = isSell ? 'Net P&L' : 'Equity';
  const equityRowTooltip = isSell
    ? 'Net P&L if you close at this scenario: (avg cost − simulated premium) × contracts × 100. Positive means profit; negative means buyback costs more than what you collected.'
    : 'Projected total equity value (contracts x simulated premium x 100) at each scenario.';

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-3">
      {/* Grid: 3 columns (Negative | Label | Positive) */}
      <div className="space-y-2 text-xs">
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-2 text-center border-b border-gray-700 pb-1">
          <span className="text-red-400 text-[10px] font-semibold">-{percentage}%</span>
          <span className="text-gray-500 text-[9px]"></span>
          <span className="text-green-400 text-[10px] font-semibold">+{percentage}%</span>
        </div>

        {/* Underlying */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <span className="text-white text-[10px]">{formatCurrency(negativeData.underlying)}</span>
          <span className="text-gray-400 text-[10px] cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content={`Projected stock price. Left: if stock drops ${percentage}%. Right: if stock rises ${percentage}%.`}
          >Underlying</span>
          <span className="text-white text-[10px]">{formatCurrency(positiveData.underlying)}</span>
        </div>

        {/* Premium */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <span className="text-white text-[10px]">{formatCurrency(negativeData.premium)}</span>
          <span className="text-gray-400 text-[10px] cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content={`Projected option premium. Left: bearish scenario (-${percentage}%). Right: bullish scenario (+${percentage}%).`}
          >Premium</span>
          <span className="text-white text-[10px]">{formatCurrency(positiveData.premium)}</span>
        </div>

        {/* % Change */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <span className={`${getValueColorClass(negativeData.premiumChange)} text-[9px]`}>
            {formatPercentWithSign(negativeData.premiumChange, 1)}
          </span>
          <span className="text-gray-400 text-[10px] cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Premium % change from current premium to the simulated premium at each scenario."
          >% Change</span>
          <span className={`${getValueColorClass(positiveData.premiumChange)} text-[9px]`}>
            {formatPercentWithSign(positiveData.premiumChange, 1)}
          </span>
        </div>

        {/* Total % Change (from cost basis to simulated premium) */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <span className={`${getValueColorClass(negativeData.totalPremiumChange)} text-[9px]`}>
            {formatPercentWithSign(negativeData.totalPremiumChange, 1)}
          </span>
          <span className="text-gray-400 text-[10px] cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Total % change from your avg cost per contract to the simulated premium."
          >Total % Chg</span>
          <span className={`${getValueColorClass(positiveData.totalPremiumChange)} text-[9px]`}>
            {formatPercentWithSign(positiveData.totalPremiumChange, 1)}
          </span>
        </div>

        {/* Equity (Buys) / Net P&L (Sells) */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <span className={`${isSell ? getValueColorClass(negativeData.equity) : 'text-white'} text-[10px]`}>
            {formatCurrency(negativeData.equity)}
          </span>
          <span className="text-gray-400 text-[10px] cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content={equityRowTooltip}
          >{equityRowLabel}</span>
          <span className={`${isSell ? getValueColorClass(positiveData.equity) : 'text-white'} text-[10px]`}>
            {formatCurrency(positiveData.equity)}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * SimulationScenariosSection - Main component with responsive grid layout
 */
function SimulationScenariosSection({ contract, simulationData, loading, error }) {
  // Extract scenario data for each percentage
  const getScenarioData = (scenarioLabel) => {
    if (!simulationData || !simulationData.length) return null;
    return simulationData.find(row => row['Underlying Scenario % Change'] === scenarioLabel);
  };

  // Parse scenario into positive and negative data
  const parseScenario = (percentage) => {
    const scenarioRow = getScenarioData(`±${percentage}%`);

    if (!scenarioRow) {
      return {
        isSell: false,
        positiveData: { underlying: 0, premium: 0, premiumChange: 0, totalPremiumChange: 0, equity: 0 },
        negativeData: { underlying: 0, premium: 0, premiumChange: 0, totalPremiumChange: 0, equity: 0 }
      };
    }

    const isSell = String(scenarioRow['Position Type'] || '').toLowerCase() === 'sell';
    const avgCost = scenarioRow['Average Cost per Contract'];
    const simPremiumPlus = scenarioRow['Simulated Premium (+)'];
    const simPremiumMinus = scenarioRow['Simulated Premium (-)'];
    const numContracts = Number(scenarioRow['Number of Contracts']) || 1;

    // Total % Chg from avg cost. For sells, profit comes from premium
    // falling, so flip the numerator's sign.
    const totalChangePlus = avgCost > 0
      ? ((isSell ? (avgCost - simPremiumPlus)  : (simPremiumPlus  - avgCost)) / avgCost) * 100
      : 0;
    const totalChangeMinus = avgCost > 0
      ? ((isSell ? (avgCost - simPremiumMinus) : (simPremiumMinus - avgCost)) / avgCost) * 100
      : 0;

    // For sells, the bottom row shows "Net P&L" instead of "Equity" (close
    // cost). Net P&L is dollar gain from the seller's cost basis at scenario.
    const equityPlus = isSell
      ? (avgCost - simPremiumPlus)  * numContracts * 100
      : scenarioRow['Simulated Equity (+)'];
    const equityMinus = isSell
      ? (avgCost - simPremiumMinus) * numContracts * 100
      : scenarioRow['Simulated Equity (-)'];

    return {
      isSell,
      positiveData: {
        underlying: scenarioRow['Simulated Underlying (+)'],
        premium: simPremiumPlus,
        premiumChange: scenarioRow['Simulated Premium (+) % Change'],
        totalPremiumChange: totalChangePlus,
        equity: equityPlus,
      },
      negativeData: {
        underlying: scenarioRow['Simulated Underlying (-)'],
        premium: simPremiumMinus,
        premiumChange: scenarioRow['Simulated Premium (-) % Change'],
        totalPremiumChange: totalChangeMinus,
        equity: equityMinus,
      }
    };
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
        <span className="text-sm text-gray-400">Loading scenarios...</span>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="py-4">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  // Render empty state
  if (!simulationData || simulationData.length === 0) {
    return <div className="text-sm text-gray-400 py-4">No simulation data available</div>;
  }

  // Main render with 5 scenario cards (compact view for contract cards)
  const scenarios = [5, 10, 20, 35, 50];

  // Get days to exit from first simulation row
  const daysToTarget = simulationData?.[0]?.['Days to Target'];

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-gray-300">Scenarios</h4>
        {daysToTarget && (
          <span className="text-[10px] text-gray-400">
            {daysToTarget} days to exit
          </span>
        )}
      </div>

      {/* Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {scenarios.map(pct => {
          const { positiveData, negativeData, isSell } = parseScenario(pct);
          return (
            <ScenarioCard
              key={pct}
              percentage={pct}
              positiveData={positiveData}
              negativeData={negativeData}
              isSell={isSell}
            />
          );
        })}
      </div>

      {/* Footer info */}
      <div className="text-[9px] text-gray-400 text-center">
        {simulationData.length} scenario{simulationData.length !== 1 ? 's' : ''} calculated
      </div>
    </div>
  );
}

export default SimulationScenariosSection;
